// Wraps the Supabase client so that it can be imported and used in any component without having to
// manually create the client each time.

/**
 * Known Issue: Supabase Connection Management
 * 
 * Background:
 * Supabase uses WebSocket connections for realtime features and maintains
 * connection state for auth and database queries. These connections can
 * become unresponsive when a browser tab loses and regains focus.
 * 
 * Our Configuration:
 * 1. Auth: Persistent sessions with auto-refresh enabled
 * 2. Realtime: Configured for optimal performance with reconnection handling
 * 3. Global error handling: Attempts reconnection on fetch errors
 * 
 * See visibilityHandler.ts for additional tab visibility management.
 */

import { createClient } from '@supabase/supabase-js'
import { Database } from '@/database.types'
import { env } from './config'

let isAuthorized = false

// Set up auth state change listener
document.addEventListener('supabase.auth.signOut', () => {
  isAuthorized = false
  supabase.realtime.disconnect()
})

document.addEventListener('supabase.auth.signIn', () => {
  isAuthorized = true
  supabase.realtime.connect()
})

// Debounce helper to prevent rapid-fire error logging
const debounce = <T extends (...args: any[]) => any>(fn: T, ms = 5000) => {
  let timeoutId: ReturnType<typeof setTimeout>
  return function (this: any, ...args: Parameters<T>) {
    clearTimeout(timeoutId)
    timeoutId = setTimeout(() => fn.apply(this, args), ms)
  }
}

// Debounced error logging
const logError = debounce((message: string, ...args: any[]) => {
  if (env.NODE_ENV === 'development') {
    console.error(message, ...args)
  }
})

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY

if (!supabaseUrl || !supabaseAnonKey) {
  throw new Error('Missing Supabase environment variables')
}

export const supabase = createClient<Database>(supabaseUrl, supabaseAnonKey, {
  auth: {
    autoRefreshToken: true,
    persistSession: true,
    detectSessionInUrl: true
  },
  global: {
    headers: {
      'Content-Type': 'application/json',
      'Accept': 'application/json'
    }
  }
})

// Track connection attempts to prevent excessive reconnections
let reconnectionAttempts = 0
const MAX_RECONNECTION_ATTEMPTS = 5
const RECONNECTION_RESET_TIMEOUT = 60000 // 1 minute

// Add a connection state listener using the channel API for debugging
// This helps track connection state changes, particularly during tab switches
const statusChannel = supabase.channel('status')
statusChannel
  .subscribe((status) => {
    if (status === 'SUBSCRIBED') {
      reconnectionAttempts = 0
      if (env.NODE_ENV === 'development') {
        console.log('Realtime connected')
      }
    }
    if (status === 'CLOSED') {
      if (env.NODE_ENV === 'development') {
        console.log('Realtime disconnected')
      }
    }
    if (status === 'CHANNEL_ERROR') {
      if (isAuthorized && reconnectionAttempts < MAX_RECONNECTION_ATTEMPTS) {
        reconnectionAttempts++
        logError('Realtime channel error - attempt ${reconnectionAttempts}/${MAX_RECONNECTION_ATTEMPTS}')
        setTimeout(() => {
          if (isAuthorized) {
            supabase.realtime.connect()
          }
        }, 1000 * reconnectionAttempts)
      }
    }
  })

// Reset reconnection attempts periodically
setInterval(() => {
  if (isAuthorized) {
    reconnectionAttempts = 0
  }
}, RECONNECTION_RESET_TIMEOUT)

/**
 * Force a Supabase reconnection
 * Used by the visibility handler when a tab becomes visible again
 * This attempts to recover from the "stuck" connection state
 */
export const reconnectSupabase = () => {
  if (isAuthorized && reconnectionAttempts < MAX_RECONNECTION_ATTEMPTS) {
    if (env.NODE_ENV === 'development') {
      console.log('Forcing Supabase reconnection')
    }
    reconnectionAttempts++
    return supabase.realtime.connect()
  }
  return Promise.resolve()
}

export const subscribeToDashboardUpdates = (brandId: string, callback: (payload: any) => void) => {
  return supabase
    .channel('dashboard_updates')
    .on(
      'postgres_changes',
      {
        event: '*',
        schema: 'public',
        table: 'dashboard_metrics',
        filter: `brand_id=eq.${brandId}`,
      },
      callback
    )
    .subscribe()
}

// Handle auth errors
export const handleAuthError = async (error: any) => {
  if (error?.status === 401) {
    isAuthorized = false
    supabase.realtime.disconnect()
    window.location.href = '/'
    return
  }
  throw error
}
